import React, { Fragment, useMemo } from 'react';

import {
  FormControl,
  Input,
  Grid,
  Text,
  Heading,
  FormErrorMessage,
} from '@chakra-ui/react';

import OwnSelect, { SelectedValues } from 'components/OwnSelect';

import { Budget } from 'types/budget';

import { BaseConfig, FormErrors } from '../types';

type BasesTabProps = {
  bases: Budget['bases'];
  processedBases: string[];
  baseConfig: BaseConfig;
  setBaseConfig: React.Dispatch<React.SetStateAction<BaseConfig>>;
  configErrors: FormErrors;
};

const BasesTab: React.FC<BasesTabProps> = ({
  bases,
  processedBases,
  baseConfig,
  setBaseConfig,
  configErrors,
}) => {
  const basesToSelect = useMemo(() => {
    return processedBases.map((base) => ({
      value: base,
      label: base,
    }));
  }, [processedBases]);

  return (
    <>
      <Heading as="h3" fontWeight="medium" fontSize="md" mb={4}>
        Agora, identifique quais bases do orçamento correspondem às bases
        encontradas no arquivo importado.
      </Heading>

      <Grid
        w="100%"
        templateColumns="repeat(auto-fit, minmax(250px, 1fr))"
        columnGap={2}
        rowGap={4}
      >
        <Text fontWeight="bold" mb={4}>
          Orçamento
        </Text>
        <Text fontWeight="bold" mb={4}>
          Bases encontradas
        </Text>
      </Grid>

      <Grid
        w="100%"
        templateColumns="repeat(auto-fit, minmax(250px, 1fr))"
        columnGap={2}
        rowGap={4}
      >
        {bases.map((base) => {
          const otherSelectedValues = Object.entries(baseConfig)
            .filter(([id]) => Number(id) !== base.id)
            .flatMap(([, values]) => values);

          const optionsWithDisabling = basesToSelect.map((option) => ({
            ...option,
            isDisabled: otherSelectedValues.includes(option.value),
          }));

          return (
            <Fragment key={base.id}>
              <FormControl>
                <Input
                  type="text"
                  value={base.version.base.description}
                  readOnly
                  _readOnly={{ background: 'white' }}
                />
              </FormControl>

              <FormControl isInvalid={!!configErrors[base.id]}>
                <OwnSelect
                  placeholder="Selecione"
                  options={optionsWithDisabling}
                  isMulti
                  isInvalid={!!configErrors[base.id]}
                  value={
                    baseConfig[base.id]?.map((value) => ({
                      value,
                      label: basesToSelect.find((b) => b.value === value)
                        ?.label,
                    })) || []
                  }
                  isClearable
                  onChange={(selected: SelectedValues) => {
                    const value = selected.map((s) => s.value);

                    setBaseConfig((prev) => ({
                      ...prev,
                      [base.id]: value,
                    }));
                  }}
                />

                <FormErrorMessage>Preencha com a base correta</FormErrorMessage>
              </FormControl>
            </Fragment>
          );
        })}
      </Grid>
    </>
  );
};

export default BasesTab;
